import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { DailySupply } from './dailySupplies';
import { DailyTruckRide } from './dailyTrucksRides';
import { DailyTruck } from './dailyTrucks';
import { User } from '../users';
import { DailyWaste } from './dailyWastes';
import { Machine } from '../materials/machines';
import { Tool } from '../materials/tools';
import { Truck } from '../materials/trucks';
import { Site } from '../sites/sites';
import { ActivityType } from './activityTypes';
import { Report } from './reports';
import { DailyTruckPlanned } from './dailyTrucksPlanned';
import { DailyMachine } from './dailyMachines';
import { DailyTool } from './dailyTools';
import { DailyRental } from './dailyRentals';
import { DailyTempWorker } from './dailyTempWorkers';
import { DailySubContractor } from './dailySubContractors';

export interface ValidationSiteSummary {
    site: Site;
    nbActivities: number;
    nbValidatedActivities: number;
    nbInValidationActivities: number;
    nbMissingActivities: number;
    siteState: number;
    totalDailyPrice: number;
}

export interface ValidationSiteSummaryResponse {
    message: string;
    data: ValidationSiteSummary[];
}

export interface ValidationSiteSummaryQueries {
    date: string;
}

export const getValidationSitesSummary = async (queries?: ValidationSiteSummaryQueries): Promise<ValidationSiteSummaryResponse> => {
    try {
        const response: AxiosResponse<ValidationSiteSummaryResponse> = await apiClient.get(`/activities/validation/sites`, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface DailyTruckRideSiteReport extends DailyTruckRide {
    site?: Site;
    user?: User;
}
export interface SiteReport {
    site: Site;
    activities: DataItem[];
    dailyMachines: DailyMachine[];
    dailyTools: DailyTool[];
    dailyRentals: DailyRental[];
    dailySupplies: DailySupply[];
    dailyTempWorkers: DailyTempWorker[];
    dailySubContractors: DailySubContractor[];
    dailyTrucks: DailyTruck[];
    dailyManagerTruckRides: DailyTruckRideSiteReport[];
    dailyDriversTruckRides: DailyTruckRideSiteReport[];
    dailyExternalManagersTruckRides: DailyTruckRideSiteReport[];
    dailyExternalDriversTruckRides: DailyTruckRideSiteReport[];
    dailyWastes: DailyWaste[];
}

export interface SiteReportResponse {
    message: string;
    data: SiteReport;
}

export interface SiteReportQueries {
    date: string;
}

export const getSiteReport = async (siteId: string, stepMode: number, queries: SiteReportQueries): Promise<SiteReportResponse> => {
    try {
        const response: AxiosResponse<SiteReportResponse> = await apiClient.get(`/activities/validation/site/${siteId}/stepMode/${stepMode}/report`, {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface MissingReportsResponse {
    message: string;
    data: User[];
}

export interface ValidateSiteActivitiesResponse {
    message: string;
    data: any;
}

export interface CloseValidationForDayResponse {
    message: string;
    data: { validatedReportsCount: number };
}

export const getUsersMissingReports = async (siteId: string, queries: { date: string }): Promise<MissingReportsResponse> => {
    try {
        const response: AxiosResponse<MissingReportsResponse> = await apiClient.get(`/activities/validation/site/${siteId}/missing-reports`, {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface ValidateActivitiesBody {
    date: string;
    activityState: number;
}

export const validateSiteActivities = async (siteId: string, body: ValidateActivitiesBody): Promise<ValidateSiteActivitiesResponse> => {
    try {
        const response: AxiosResponse<ValidateSiteActivitiesResponse> = await apiClient.patch(`/activities/validation/site/${siteId}/validate`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface TransferDailyElementsBody {
    sourceActivityId: string;
    targetActivityId: string;
}

export interface TransferDailyElementsResponse {
    message: string;
    data: {
        transferredElementsCount: number;
    };
}

export const transferDailyElements = async (body: TransferDailyElementsBody): Promise<TransferDailyElementsResponse> => {
    try {
        const response: AxiosResponse<TransferDailyElementsResponse> = await apiClient.patch(`/activities/validation/transfer-daily`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface DailyItemSiteQuantity {
    site: Site;
    quantity: number;
}

export interface DailyUserItem {
    user: User;
    sites: DailyItemSiteQuantity[];
}

export interface DailyMachineItem {
    machine: Machine;
    sites: DailyItemSiteQuantity[];
}

export interface DailyToolItem {
    tool: Tool;
    sites: DailyItemSiteQuantity[];
}

export interface DailyTruckItem {
    priceType: number;
    truck: Truck;
    sites: DailyItemSiteQuantity[];
}

export interface DailyItemsByDate {
    date: string;
    users: DailyUserItem[];
    machines: DailyMachineItem[];
    tools: DailyToolItem[];
    trucks: DailyTruckItem[];
}

export interface DailyItemsByDateResponse {
    message: string;
    data: DailyItemsByDate;
}

export const getDailyItemsWithQuantityByDate = async (date: string): Promise<DailyItemsByDateResponse> => {
    try {
        const response: AxiosResponse<DailyItemsByDateResponse> = await apiClient.get(`/activities/validation/daily-items/${date}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export interface DeleteActivityResponse {
    message: string;
    data: string;
}

export const deleteActivity = async (activityId: string): Promise<DeleteActivityResponse> => {
    try {
        const response: AxiosResponse<DeleteActivityResponse> = await apiClient.delete('/activities/activity/' + activityId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface Activity {
    _id: string;
    activityTypeId: string;
    reportId: string;
    activityState: number;
    siteId: string;
    startTime: string;
    validationTime?: string;
    validationTimestamp?: string;
    observationsPlanning?: string;
    observationsWorker?: string;
    needsValidationTime: string;
    planningValidationTime: string;
    isTruckManager: boolean;
    needSubmitValidationTimeStamp?: string;
    planningSubmitValidationTimeStamp?: string;
    stepMode: number;
    isSpecialHidden?: boolean;
    isHighlight?: boolean;
    includedInExpenses?: boolean;
    isLongDistance?: boolean;
    longDistancePrice?: number;
}

export interface CreateActivityBody {
    activityTypeId: string;
    siteId: string;
    userId: string;
    startTime: string;
    state?: number;
    activityState: number;
    reportState: number;
    planningValidationTime?: string;
    needsValidationTime?: string;
    stepMode: number;
    observationsWorker?: string;
    observationsPlanning?: string;
    includedInExpenses?: boolean;
    isTruckManager?: boolean;
    quantity?: number;
}

export interface ActivityResponse {
    message: string;
    data: Activity;
}

export const createActivity = async (activityData: CreateActivityBody): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.post('/activities', activityData);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdatedActivity {
    message: string;
    data: DataItem;
}

export const updateActivity = async (activityId: string, updateData: any): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('/activities/activity/' + activityId, updateData);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateActivitiesStateBody {
    activityIds: string[];
    activityState: number;
    currentState: number;
}

export interface UpdateActivitiesStateQueries {
    freezePrices?: boolean;
}

export const updateActivitiesState = async (body: UpdateActivitiesStateBody, queries?: UpdateActivitiesStateQueries): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('/activities/state', body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const updateActivityTruckManager = async (activityId: string, isTruckManager: boolean): Promise<ActivityResponse> => {
    try {
        const response: AxiosResponse<ActivityResponse> = await apiClient.patch('/activities/activity/' + activityId + '/isTruckManager', {
            isTruckManager,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importActivitiesBySite = async (
    siteId: string,
    date: string,
    fromState: number,
    toState: number,
    stepMode: number
): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch(
            '/activities/site/' + siteId + '/date/' + date + '/stepMode/' + stepMode + '/state',
            {
                fromState,
                toState,
            }
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importLastNeeds = async (siteId: string, date: string): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.post('/activities/import', { siteId, date });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DataItem {
    _id: string;
    activityTypeId: ActivityType;
    reportId: Report;
    activityState: number;
    siteId: Site;
    startTime: string;
    validationTime?: string;
    validationTimestamp?: string;
    observationsPlanning?: string;
    observationsWorker?: string;
    needsValidationTime: string;
    planningValidationTime: string;
    isTruckManager: boolean;
    dailyMachines: DailyMachine[];
    dailyTools: DailyTool[];
    dailyRentals: DailyRental[];
    dailySubContractors: DailySubContractor[];
    dailyTempWorkers: DailyTempWorker[];
    dailyTrucksPlanned: DailyTruckPlanned[];
    dailyTrucksRides: DailyTruckRide[];
    dailySupplies: DailySupply[];
    needSubmitValidationTimeStamp?: string;
    planningSubmitValidationTimeStamp?: string;
    stepMode: number;
    isSpecialHidden?: boolean;
    isHighlight?: boolean;
    includedInExpenses?: boolean;
    isLongDistance?: boolean;
    longDistancePrice?: number;
    hasPendingInfos?: boolean;
    quantity: number;
    unitUserPrice?: number;
    unitVehiclePrice?: number;
    unitLongTripPrice?: number;
}

export interface ActivitiesResponse {
    message: string;
    data: DataItem[];
}

export interface GetActivitiesQueries {
    startDate?: string;
    endDate?: string;
    stepMode?: number;
    userId?: string;
    conducId?: string;
}

export const getActivities = async (queries?: GetActivitiesQueries): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.get('/activities', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
